// ──────────────────────────────────────────────────────────────────────────
// LA GUIDA
//
// I testi che il maestro legge nel profilo (Guida), che la ricerca scorre e
// che i "?" sparsi per l'app aprono. Stanno qui, e solo qui: la scheda, la
// ricerca e i "?" leggono tutti da questo file, così una cosa che cambia si
// corregge in un posto solo. Quando cambia un comportamento che un maestro
// vede, si aggiorna anche il suo argomento (CLAUDE.md, Documentazione).
//
// Un argomento è fatto di blocchi:
//   { p: "…" }                        un paragrafo
//   { h: "…" }                        un titoletto
//   { steps: ["…", "…"] }             passi numerati
//   { list: ["…", "…"] }              un elenco
//   { choices: [{ title, text }] }    scelte una sotto l'altra, nei blocchi
// Nei testi **così** è grassetto, e nient'altro: niente link né HTML.
//
// I nomi di bottoni e voci sono quelli che l'app scrive davvero: chi legge
// li cerca sullo schermo con gli occhi.
// ──────────────────────────────────────────────────────────────────────────

export const GUIDE_TOPICS = [
	{
		id: "cominciare",
		title: "Per cominciare",
		hint: "registrarsi, il circolo, i primi passi",
		blocks: [
			{
				p: "Ci si registra in due minuti, e serve solo sapere una cosa: hai il codice di un circolo, o no?",
			},
			{
				steps: [
					"Tocca **Inizia gratis**, scegli email e password (almeno 8 caratteri) e tocca **Crea l'accesso**.",
					"**Creo il mio circolo** se insegni per conto tuo, o sei il primo del tuo circolo a usare l'app: ti chiede il tuo nome, che ruolo hai nel circolo, il circolo, i campi e come ti pagano gli allievi. **Ho il codice di un circolo** se un collega ti ha dato un codice: prima di entrare l'app ti dice in che circolo e con che ruolo.",
					"Tocca **Crea il tuo spazio**, o **Entra nel circolo**: sei dentro.",
				],
			},
			{
				p: "Nel profilo trovi i **Primi passi**: il primo allievo, la prima lezione, i prezzi, Google Calendar. Ogni riga ti porta dove si fa e sparisce quando l'hai fatta.",
			},
			{
				p: "Al ruolo rispondi **Ci insegno** se il circolo lo dirige qualcun altro: nei Primi passi potrai proporre l'app alla direzione. **Lo dirigo, e ci insegno** se è tuo. **Lo dirigo, ma non insegno** se sei il direttore o la segreteria: non ti chiede come ti pagano, e nei Primi passi ti propone di far entrare i maestri.",
			},
			{
				p: "Campi, prezzi e tariffe si cambiano quando vuoi, dal profilo: non serve averli giusti il primo giorno.",
			},
		],
	},
	{
		id: "allievi",
		title: "Gli allievi",
		hint: "aggiungerli, importarli, archiviarli",
		blocks: [
			{
				p: "Gli allievi sono del circolo: li vedono anche i colleghi che ci insegnano, e chiunque può metterli a lezione.",
			},
			{
				list: [
					"**Uno alla volta**: dalla pagina Allievi, tocca il **+**. Nome, cognome e, se vuoi, il **soprannome**, come lo chiami in campo: distingue due omonimi, e nelle lezioni puoi scrivere quello.",
					"**Mentre segni una lezione**: scrivi un nome che non c'è ancora, e l'allievo nasce con la lezione.",
					"**Tutti insieme**: tocca **Importa da un foglio**, sotto il titolo della pagina, e incolla le colonne dal foglio di calcolo. L'app capisce da sola quale colonna è cosa, e salta i nomi che ci sono già.",
				],
			},
			{
				p: "La **scheda tecnica** (bottone **Scheda**, in fondo all'allievo) dice com'è oggi: livello PTR, tipo di giocatore e su cosa state lavorando, con una nota per ognuno. Niente voti: quello che avete fatto lo raccontano le lezioni.",
			},
			{
				p: "Un allievo che non viene più non si cancella: si **archivia**, e lezioni e pagamenti restano dove sono. Lo ritrovi nell'archivio, il bottone con la scatola accanto alla ricerca, e con **Riattiva** torna tra gli attivi.",
			},
		],
	},
	{
		id: "lezione",
		title: "Segnare una lezione",
		hint: "tre schermate, e una lezione che si ripete",
		featured: true,
		blocks: [
			{
				p: 'Una lezione si segna in tre schermate, dal **+** in basso nella pagina Lezioni. Se usi "Gioco io" il + chiede **Lezione** o **Gioco io**; tenuto premuto, va dritto alla lezione.',
			},
			{
				steps: [
					"**Quando e cosa**: il tipo (Privata, Scuola tennis, Corso adulti e le tipologie del circolo), la data, l'ora di inizio (quella di fine si propone da sola), quanti giocatori e il campo. Per la scuola tennis e il corso adulti puoi dire il livello, se vuoi.",
					"**Chi c'è**: un nome per giocatore, e come paga. Il prezzo arriva dal tuo listino, e lo puoi cambiare per quella volta. Nei corsi i nomi si possono lasciare vuoti e scrivere quando li sai.",
					"**Riepilogo**: controlli, aggiungi una nota se serve, e tocchi **Conferma**.",
				],
			},
			{
				p: "Per una lezione che si ripete, nel riepilogo accendi **Ripeti lezione** e scegli fino a quando: l'app ne crea una per settimana, e ognuna poi vive la sua vita.",
			},
			{
				p: "Se a quell'ora hai già una lezione, o il campo è già di un collega, sotto gli orari l'app ti dice cosa c'è e per quanto vi sovrapponete. Puoi salvarla lo stesso; in agenda, accanto all'orario delle lezioni che si accavallano, resta un triangolino arancione.",
			},
			{
				p: "Se un allievo paga il circolo invece di te, per esempio in segreteria, accendi **Ha pagato al circolo**: nei conti finisce tra quello che fatturi al circolo, non tra i tuoi incassi.",
			},
		],
	},
	{
		id: "giorno-settimana",
		title: "Giorno e settimana",
		hint: "come ci si muove tra le lezioni",
		blocks: [
			{
				p: "In alto a sinistra ci sono i due bottoni per passare dal **giorno** alla **settimana**. In basso le frecce spostano di un giorno, o di una settimana se le tieni premute; il calendario salta a una data.",
			},
			{
				list: [
					"Sul telefono **stringi due dita** sul giorno per vedere la settimana, e **allargale** per tornare al giorno.",
					"Nella settimana, arrivato in cima **continua a tirare giù** e vai alla settimana prima; arrivato in fondo **continua a tirare su** e vai a quella dopo. Quando il cerchio è pieno, lascia.",
					"Nella settimana ogni lezione è una riga: toccandola vai al suo giorno, con la lezione aperta.",
					"Su una lezione, **trascina verso destra** per duplicarla e **verso sinistra** per modificarla.",
				],
			},
			{
				p: "Da quale partire lo scegli nel profilo, alla voce **Vista delle lezioni**.",
			},
			{
				p: "Se insegni in più circoli, nel profilo puoi accendere le ore che passi altrove: le vedi sbiadite accanto a quelle di qui, e se segni una lezione quando sei già altrove l'app te lo dice.",
			},
		],
	},
	{
		id: "a-lezione",
		title: "A lezione",
		hint: "presenze, sostituti, il programma",
		featured: true,
		blocks: [
			{
				p: "Tocca una lezione per aprirla: dentro ci sono chi c'è, il campo, il programma e in fondo **Elimina**, **Modifica** e **Duplica**.",
			},
			{ h: "Presenze" },
			{
				p: "In un corso, sotto **Presenze abbonati** c'è una riga per ogni abbonato con **P**. Toccala e diventa **A**, assente; toccala di nuovo e torna presente. Ogni abbonato nasce presente: si segna solo chi manca.",
			},
			{ h: "Sostituti" },
			{
				p: "Sotto un assente compare **Aggiungi sostituto**: scegli chi viene al suo posto e come paga. Prima di scrivere un nome ti propone chi potrebbe venire, anche tra gli allievi dei colleghi: in cima chi ha un recupero di quel corso (**Hanno un recupero del corso adulti**), sotto **Pagherebbero la volta**, chi gioca a un livello vicino; chi gioca solo nell'altro corso non te lo propone: per questo servono il **Livello PTR** del corso adulti o quello nella **Scheda tecnica** degli allievi, e se mancano te lo dice. Toccane uno per sceglierlo: non avvisa nessuno. Non cambia né il tipo né il prezzo della lezione, e se l'assente torna presente il sostituto se ne va.",
			},
			{ h: "Il programma" },
			{
				p: "Il riquadro del programma prima della lezione chiede **Cosa facciamo?**: dentro scegli su cosa si lavora e scrivi le quattro fasi, riscaldamento, fase tecnica, fase tattica, defaticamento. Accanto a ogni esercizio, il campo ne disegna lo schema. L'obiettivo lo vedi anche a lezione chiusa, sotto i nomi. È tutto facoltativo, non va su Google Calendar e non si copia quando duplichi o ripeti una lezione. Quello che rifai spesso tienilo nel **Quaderno**.",
			},
			{ h: "Com'è andata?" },
			{
				p: "L'ultima lezione appena finita ha in fondo un biglietto da staccare: **Vuoi aggiungere due note?**, fino a sera o finché non le scrivi. Scritte, passa alla lezione prima; quelle delle altre lezioni le scrivi aprendole. Lì scegli su cosa avete lavorato, scrivi due righe nel **Diario** e, in **Su qualcuno in particolare?**, tocchi un allievo e scrivi una riga su di lui: la ritrova nelle sue **Lezioni**, sotto questa. Vale anche senza programma. Lo stesso foglio si apre toccando il riquadro del programma nella lezione aperta: prima della lezione apre il programma, dopo **Com'è andata?**.",
			},
			{
				p: "Nell'evento di Google Calendar, sotto le note, c'è **Apri la lezione in inCampo**: il link apre l'app con la lezione aperta.",
			},
		],
	},
	{
		id: "schema",
		title: "Lo schema di un esercizio",
		hint: "campo, giocatori e frecce",
		blocks: [
			{
				p: "Nel programma, accanto a ogni esercizio, il campo apre lo schema: lo disegni come una scheda, e sotto l'esercizio resta la miniatura. Toccala, o **Modifica**, per riaprirlo; la ✕ accanto lo toglie.",
			},
			{ h: "Comporlo" },
			{
				list: [
					"Tocca un pezzo in basso, **Giocatore**, **Maestro**, **Coni/coppelle**, **Zona** o **Numero**: va in campo, e lo trascini dove vuoi. Toccando una coppella la fai diventare un **Cono**, e viceversa.",
					"Per una freccia tocca **Tesa** o **Tonda** per la palla, **Spostamento** per chi si muove, e tirala sul campo da dove parte a dove arriva.",
					"Il pallino a metà di una freccia la piega, e **Gira** curva la tonda dall'altra parte. Il tasto con la doppia freccia mette la punta anche alla partenza: un palleggio, o chi va e torna, in una freccia sola.",
					"Toccando una sagoma ne cambi il colore, la fai trasparente con **Ombra** e le dai un numero con **N.**",
					"Trascinando, un pezzo o la punta di una freccia si aggancia quando è in fila con un altro pezzo o con una riga del campo, e la riga arancione lo fa vedere. Per metterlo un filo più in là, mentre trascini tocca **Libero**.",
					"Toccato un pezzo o una freccia, sopra c'è **Duplica**: la copia arriva un metro più in là, pronta da spostare.",
				],
			},
			{
				p: "In cima scegli se comporlo **In prospettiva**, come lo vedrai, o **Dall'alto**, dove in fondo al campo i pezzi non rimpiccioliscono: lo schema è lo stesso. La freccia tonda in alto annulla l'ultima mossa, **Fatto** lo lascia all'esercizio e si salva col programma. **Togli lo schema**, in basso, lo toglie tutto.",
			},
			{
				p: "Nella lezione aperta lo schema sta accanto all'esercizio: toccalo e si apre grande. Se salvi l'esercizio nel **Quaderno**, lo schema va con lui.",
			},
			{
				p: "Su che campo disegnarli lo scegli nel profilo, alla voce **Superficie degli schemi**: **Cemento**, **Terra battuta** o **Erba**. Cambia il colore di tutti gli schemi, su questo telefono, e non tocca quello che hai disegnato.",
			},
		],
	},
	{
		id: "quaderno",
		title: "Il quaderno",
		hint: "lezioni ed esercizi da riusare",
		blocks: [
			{
				p: "Nel **Quaderno**, in basso tra Lezioni e Profilo, tieni le lezioni e gli esercizi che rifai spesso. È tuo: lo cambi solo tu, e i colleghi vedono solo quello che condividi.",
			},
			{
				list: [
					"**Lezioni**: un programma intero, con obiettivo, su cosa si lavora e le quattro fasi.",
					"**Esercizi**: uno solo, di una fase precisa o buono per **Qualsiasi fase**.",
				],
			},
			{ h: "Dal programma di una lezione" },
			{
				list: [
					"In cima, **Prendi una lezione dal quaderno**; se viene già dal quaderno, **Cambia**.",
					"Accanto a ogni fase, il libro prende un esercizio dal quaderno e lo aggiunge in fondo.",
					"Accanto a ogni esercizio, il segnalibro lo salva nel quaderno. Pieno vuol dire che c'è già.",
					"In fondo, **Salva nel quaderno** salva il programma intero.",
				],
			},
			{
				p: "Quello che prendi si copia nella lezione: se poi cambi il quaderno, le lezioni già fatte restano come sono. Un esercizio preso e cambiato lo dice: **Dal quaderno**, e accanto **cambiato**.",
			},
			{
				p: "Se poi un esercizio lo cambi nel quaderno (gli disegni lo schema, ne correggi il testo), nelle lezioni che l'avevano preso compare **Un esercizio è cambiato nel quaderno**: tocca **Guarda**, e sotto l'esercizio **Aggiorna** lo riprende com'è adesso. Diventa così quando tocchi **Salva**; se non lo tocchi, la lezione resta com'è. Lo stesso nelle lezioni del quaderno, quando le modifichi.",
			},
			{
				p: "Se la lezione ha un livello, il SAT o il PTR del CAD, il quaderno parte da quello: ogni lezione ha già scelta la variante di quel livello. Tocca la scheda per prendere quella, o un'altra variante per prendere l'altra.",
			},
			{
				p: "Se hai cambiato quello che hai preso, **Salva** ti chiede cosa farne: **Solo per questa lezione**, **Aggiorna la variante** (le lezioni già fatte non cambiano) o **Crea una variante** per un altro livello. Se è di un collega: **Solo per questa lezione** o **Salvane una copia nel mio quaderno**.",
			},
			{ h: "Le varianti per livello" },
			{
				p: "Una lezione o un esercizio può avere una variante per livello: Red, Orange, Green, Yellow o un livello PTR. Aprila: in cima ci sono le varianti, e **+ Variante** ne fa una nuova partendo da quella che guardi. Scegli il livello (quelli che ce l'hanno già sono sbiaditi) e scrivi cosa cambia rispetto alle altre. Nel quaderno, le pastiglie dei livelli mostrano solo le voci con quel livello.",
			},
			{ h: "Con i colleghi" },
			{
				p: "Aperta una voce, **Condividi con i colleghi** la fa vedere ai colleghi del circolo: la usano, ma la cambi solo tu. Le tue condivise hanno le due sagome accanto al nome, o **Condiviso** sulla copertina; quelle dei colleghi dicono di chi sono. Con il tasto a sinistra della ricerca scegli **Tutte**, **Mie** o **Dei colleghi**. Una voce di un collega la puoi usare, o salvarne una copia con **Copia nel mio quaderno**. Le tue dicono anche quante volte le hanno usate i colleghi.",
			},
			{
				p: "Se esci dal circolo, le tue voci condivise vengono via con te; uscendo puoi anche **lasciarne una copia al circolo**, che tiene chi lo dirige.",
			},
			{ h: "Le voci" },
			{
				p: "Lezioni ed esercizi stanno su due linguette, **Lezioni** ed **Esercizi**. Sopra le voci, le pastiglie le restringono: le lezioni per su cosa si lavora, gli esercizi per fase. Quelle usate di recente stanno in cima.",
			},
			{
				p: "Sotto una lezione, i quattro pallini sono le quattro fasi: pieni quelli delle fasi scritte. Accanto c'è l'ultima volta che l'hai usata.",
			},
			{
				p: "Tocca una lezione per aprirla lì dove sta: vedi le fasi con i loro esercizi e quante volte l'hai usata. Dentro, **Modifica** la cambia, o la toglie con **Togli dal quaderno** (con più varianti, **Togli la variante**): prima ti chiede, e **Sì, toglila** è il tasto rosso a sinistra.",
			},
			{
				p: "Gli esercizi li guardi in griglia o a lista, con l'interruttore accanto alle linguette. In griglia sono carte, con lo schema per copertina; chi non ce l'ha dice **Senza schema**. A lista stanno divisi per fase, con quanti sono. Toccane uno e si apre a pagina intera, con lo schema in alto: toccalo e lo vedi a schermo pieno, **Modifica schema** lo cambia, e senza schema il + in mezzo al campo lo aggiunge. In fondo, **Modifica** cambia nome, fase e testo, e **Togli dal quaderno** toglie l'esercizio, dopo averti chiesto: **Sì, toglilo** a sinistra, **No, macchè!** a destra. Si chiude con la ✕, col tasto indietro o trascinandola giù.",
			},
		],
	},
	{
		id: "abbonamenti",
		title: "Corsi in abbonamento",
		hint: "assenze, recuperi, un corso che salta",
		featured: true,
		blocks: [
			{
				p: "Alla scuola tennis e al corso adulti si viene con l'abbonamento, e ogni assenza di un abbonato è **una lezione da recuperare**. Quando l'allievo recupera, in un'altra lezione dello stesso corso scegli come pagamento **Recupero**: il corso adulti si recupera nel corso adulti, la scuola tennis nella scuola tennis. L'app lo propone solo a chi ne ha ancora, e dice quanti gliene restano.",
			},
			{
				p: "I recuperi valgono per la **stagione**, che comincia il 1° settembre. Nel profilo, **Assenze abbonati** ha un blocco per corso con chi ha lezioni da recuperare e chi le ha già recuperate: tocca il nome del corso per aprirlo o chiuderlo. In cima a ogni corso trovi i **Posti liberi** dei prossimi 7 giorni: le lezioni dove un abbonato è assente e non ha ancora un sostituto. Toccane uno per scegliere chi viene al suo posto.",
			},
			{
				p: "Anche la pagina dell'allievo lo dice: accanto a **Lezioni** leggi quante ne ha da recuperare, e aprendo il blocco trovi una riga per corso con le sue assenze e i recuperi della stagione. Toccala per vedere quali lezioni sono. Sotto un recupero c'è al posto di chi è venuto.",
			},
			{
				p: "Se l'allievo ha recuperato con un altro maestro del circolo, nel foglio delle assenze tocca quell'assenza e scegli **Recuperata con un altro maestro**: resta scritta, ma non conta più tra quelle da recuperare. Se ti sei sbagliato, dallo stesso posto c'è **Rimettila da recuperare**. Toccando l'assenza vedi anche chi è venuto al suo posto e cosa avevi scritto su di lui, e da lì **Apri la lezione**.",
			},
			{ h: "Quando un corso salta" },
			{ p: "Non eliminarlo: tocca **Elimina** e l'app ti chiede cosa è successo." },
			{
				choices: [
					{
						title: "Annulla",
						text: "Era prevista ma non si è fatta: resta in agenda come annullata, e ogni abbonato ha una lezione da recuperare.",
					},
					{
						title: "Annulla e riprogramma",
						text: "Come Annulla, e subito la lezione nuova con tutti gli abbonati in recupero.",
					},
					{
						title: "Elimina",
						text: "Era stata inserita per sbaglio: sparisce.",
					},
				],
			},
			{
				p: "Chi pagava la volta non paga niente. Una lezione annullata non conta in ore e compensi, e sparisce da Google Calendar. Annullata per sbaglio? Aprila e tocca **Ripristina**.",
			},
		],
	},
	{
		id: "cambiare-idea",
		title: "Cambiare idea",
		hint: "modificare, duplicare, eliminare",
		blocks: [
			{
				list: [
					"**Modifica** (o trascina verso sinistra) riapre il modulo con tutto quello che c'era.",
					"**Duplica** (o trascina verso destra) fa una lezione nuova con gli stessi allievi, in un altro giorno.",
					"**Elimina** la toglie, dopo averti chiesto: **Sì, eliminala** è il tasto rosso a sinistra, **No, macchè!** quello pieno. Per un corso in abbonamento prima chiede cosa è successo.",
				],
			},
			{
				p: "Su una lezione che si ripete scegli **solo questa** o **anche le prossime**: cambia quella e tutte quelle dopo, le passate restano com'erano. Presenze e sostituti restano quelli di ogni giorno. Serve la rete.",
			},
			{
				p: "Una lezione eliminata per sbaglio si rimette: nel profilo, nella scheda del circolo, apri **Lezioni eliminate** e tocca **Reinserisci**. Restano lì un anno.",
			},
			{
				p: 'Quando una lezione l\'ha messa o cambiata qualcun altro, aprendola lo leggi in fondo: "Inserita da Giulia", "Ultima modifica: Giulia, 12/10 alle 18:30".',
			},
		],
	},
	{
		id: "conti",
		title: "I conti",
		hint: "compensi, listino, costo dei campi",
		featured: true,
		blocks: [
			{
				p: "Il profilo è il riepilogo di un mese: lo sposti con le frecce, o scegli due date. I numeri li calcola l'app dalle lezioni che hai segnato.",
			},
			{
				p: "In **Ore di lezione** ogni tipo di lezione dice le ore e quante persone diverse ci sono venute: chi viene a tutte le lezioni del corso conta una volta sola. Così vedi quanti allievi hai in ogni tipo. Tocca un tipo di lezione per vedere le lezioni che ci stanno dentro.",
			},
			{
				p: "Le schede del profilo le metti nell'ordine che vuoi: **tieni premuto il titolo** di una scheda, trascina le righe dai puntini a destra e tocca **Fatto** (o torna indietro). Sul computer le schede stanno in due colonne, e si spostano anche dall'una all'altra. L'ordine vale su quel telefono o quel computer; **Torna all'ordine di partenza** lo rimette com'era.",
			},
			{
				p: "**Compensi** si apre a sezioni: **Da fatturare al circolo** (i corsi, a ore, e gli allievi che hanno pagato il circolo invece di te), **Incassi private**, **Costo campi** e, se c'è, **Da dare al circolo** (i soldi presi per conto del circolo, per esempio da un sostituto). In cima c'è il netto: quello che fatturi e incassi, meno i campi. I compensi li vedi solo tu.",
			},
			{ h: "Da impostare una volta" },
			{ p: "Nel profilo, nella scheda **Le tue condizioni**:" },
			{
				list: [
					"**Tipologie**: quali lezioni fai e il loro prezzo, in due gruppi. **Da fatturare ai privati** è quanto chiedi a ogni allievo, all'ora; **Da fatturare al circolo** è quanto ti paga il circolo per un'ora, qualunque sia il numero di allievi. Toccando il nome di un corso puoi accendere **Usa una sigla**, per leggere per esempio CAD al posto di Corso adulti.",
					"**Pagamenti**: i metodi che accetti.",
					"**Costo campo**: quanto costa il campo all'ora, con luce e riscaldamento se il circolo li fa pagare a parte, e chi lo paga.",
				],
			},
			{ h: "Chi paga il campo" },
			{
				choices: [
					{
						title: "Tu",
						text: "L'allievo paga il prezzo pieno, e il campo si toglie nei tuoi conti.",
					},
					{
						title: "Gli allievi, al circolo",
						text: "Chiedi meno: il prezzo scende della quota di campo di ognuno, e nei tuoi conti non c'è niente da togliere.",
					},
				],
			},
			{
				p: "Prezzi e tariffe valgono da quando li cambi: i mesi già passati non si muovono.",
			},
		],
	},
	{
		id: "circolo",
		title: "Il circolo e i colleghi",
		hint: "la tua foto, il logo, far entrare un collega, i ruoli, chi non insegna, passare il circolo, più circoli",
		blocks: [
			{
				p: "Nel profilo, la scheda col nome del circolo tiene i dati, i campi, i maestri e le lezioni eliminate. Se lavori in un circolo solo, sul telefono ci arrivi da ogni pagina toccando il tondo del circolo in alto a destra. Chi può cambiare cosa lo decide il proprietario: chi ha creato il circolo, o chi l'ha ricevuto da lui.",
			},
			{ h: "La tua foto" },
			{
				p: "Nel profilo, nella scheda col tuo nome, tocca il tondo con la fotocamera: si apre **La tua foto**. Tocca **Scegli una foto**, o **Scatta una foto**, sistemala nel cerchio e tocca **Usa questa**. I colleghi la vedono accanto al tuo nome: nell'elenco dei maestri, quando ti affidano una lezione e quando guardano le tue; il foglio te lo mostra sotto **Così ti vedono**. Da lì la cambi o la togli con **Togli la foto**; senza foto restano le tue iniziali. Sul telefono la tua foto è anche il tondo in basso a destra, che porta al profilo.",
			},
			{ h: "Il logo del circolo" },
			{
				p: "Se puoi cambiare i dati del circolo, nel profilo tocca il tondo del circolo, quello con la fotocamera: si apre **Il logo del circolo**. Tocca **Scegli un'immagine** o **Scatta una foto**, sistemalo nel cerchio e tocca **Usa questo**. Lo vedono tutti i maestri del circolo, in cima allo schermo. Senza logo, il circolo è la sua iniziale.",
			},
			{ h: "Far entrare un collega" },
			{
				steps: [
					"Nella scheda del circolo tocca **Maestri**, poi **Fai entrare un collega**.",
					"Scegli che ruolo avrà, tocca **Avanti**, poi se insegna nel circolo, e **Genera il codice**.",
					"Mandagli il codice: vale sette giorni, e per più persone.",
					"Lui si registra scegliendo **Ho il codice di un circolo**, o se ha già un account tocca **Entra in un altro circolo** nel suo profilo.",
				],
			},
			{
				p: "Far entrare colleghi lo fanno il proprietario e i gestori. Il ruolo lo sceglie solo il proprietario: i codici di un gestore fanno entrare colleghi. Per chi non scende in campo, un direttore o la segreteria, scegli **No, non insegna**: entra già così.",
			},
			{ h: "Chi fa cosa" },
			{
				p: "In **Maestri** chi c'è è diviso per ruolo. Tocca un nome per aprire la sua scheda: lì il proprietario sceglie il ruolo, e da lì toglie qualcuno dal circolo.",
			},
			{
				choices: [
					{ title: "Collega", text: "Segna le sue lezioni." },
					{ title: "Osservatore", text: "Vede giornata e riepilogo di tutti i maestri." },
					{
						title: "Gestore",
						text: "Modifica e assegna le lezioni di tutti, fa entrare colleghi, cambia campi, listino e dati.",
					},
					{ title: "Proprietario", text: "Tutto, e decide cosa fanno gli altri." },
				],
			},
			{
				p: "Con **Personalizza** il proprietario sceglie voce per voce, e può affidare a qualcuno le lezioni di un collega solo.",
			},
			{ h: "Chi non insegna" },
			{
				p: "Nel circolo può starci anche chi non insegna. Nella sua scheda il proprietario spegne **Insegna in questo circolo**: resta nel circolo col suo ruolo e guarda le lezioni che il ruolo gli fa vedere, ma non ha una giornata sua e non si sceglie per una lezione. Nell'elenco ha l'etichetta **non insegna**.",
			},
			{
				p: "Vale anche per il proprietario, che resta proprietario: aprendo l'app trova le lezioni dei colleghi. Le lezioni già fatte restano di chi le ha tenute, e riaccendendo l'interruttore si torna maestri come prima.",
			},
			{ h: "Proporre l'app alla direzione" },
			{
				p: "Se hai portato tu l'app in un circolo che dirige qualcun altro, nei **Primi passi** rispondi **No, ci insegno** a *è tuo?*, poi tocca **Proponi l'app a chi gestisce il circolo** e **Genera il codice**. Esce un messaggio pronto: tocca **Copia** e mandalo alla direzione o alla segreteria. Entrano da gestori, senza insegnare.",
			},
			{ h: "Passare il circolo" },
			{
				steps: [
					"In **Maestri** tocca il nome di chi deve diventare proprietario: nel circolo ci dev'essere già.",
					"Tocca **Passagli il circolo**, scegli se tu resterai gestore o collega, e tocca **Proponi a** col suo nome.",
					"Lui trova la proposta in cima al profilo e tocca **Accetta il circolo**. Finché non lo fa non cambia niente, e dalla sua scheda puoi toccare **Ritira la proposta**.",
				],
			},
			{
				p: "Dopo, decide lui chi fa cosa. Le lezioni, gli allievi e i conti restano dove sono, e tu continui a insegnare come prima.",
			},
			{ h: "Più circoli" },
			{
				p: "Passi dall'uno all'altro dal selettore in alto: sul telefono è il tondo del circolo in alto a destra, in ogni pagina; toccalo e scegli, o trascinalo in giù o in su per passare al circolo dopo o a quello prima. In **I tuoi circoli** scegli il principale, quello con cui si apre l'app, e puoi archiviare un circolo dove non lavori più: lo storico resta. Uscendo da un circolo le lezioni restano lì, e rientrando ritrovi tutto.",
			},
		],
	},
	{
		id: "gioco-io",
		title: "Gioco io",
		hint: "quando giochi tu, e il campo lo paghi tu",
		blocks: [
			{
				p: "Quando giochi tu, una partita o un allenamento tuo, il campo lo paghi tu. Lo segni dal **+** scegliendo **Gioco io**: giorno, ora, campo, luce e riscaldamento, con chi giochi. Niente allievi e niente prezzi.",
			},
			{
				p: "In agenda è una riga grigia. Non conta come lavoro: niente ore di lezione, presenze o incassi. Il campo invece sì, nell'utilizzo dei campi e nel **Costo campi** dei compensi, alla voce **Quando giochi tu**.",
			},
			{ p: "Se non giochi mai, spegnilo dal profilo, alla voce **Gioco io**." },
		],
	},
	{
		id: "calendario-rete",
		title: "Google Calendar e senza rete",
		hint: "il calendario, il campo senza segnale",
		blocks: [
			{
				p: "Nel profilo, alla voce **Google Calendar**, tocca **Collega**: da lì ogni lezione che segni, cambi o elimini finisce anche sul tuo calendario. Una volta al giorno l'app controlla che ci sia tutto e sistema quello che manca; per farlo subito, **Gestisci** e **Controlla ora**.",
			},
			{
				p: "Senza rete l'app lavora lo stesso: quello che segni parte da solo quando la rete torna. In alto a destra una pastiglia dice **Offline**, **Offline · da inviare** o **Invio in corso**.",
			},
			{
				p: "Senza rete non si può cambiare tutta una serie, né circolo, colleghi o listino, né entrare o registrarsi.",
			},
			{
				p: "Quando esce una versione nuova compare **È pronta una versione nuova**: tocca **Aggiorna**, o **Dopo**.",
			},
		],
	},
	{
		id: "problemi",
		title: "Se qualcosa non va",
		hint: "segnalare un problema",
		blocks: [
			{
				p: "In fondo al profilo tocca **Segnala un problema**: scrivi cosa è successo, di' quanto ti blocca e, se vuoi, aggiungi uno screenshot. Da quale pagina venivi e che telefono usi li allega l'app da sola. Senza rete la segnalazione parte quando la rete torna.",
			},
			{
				p: "Se l'app si inceppa, al posto della pagina c'è Pallino con **Qualcosa si è inceppato**: quello che avevi salvato è al sicuro, e la segnalazione ci arriva da sola. Se vuoi, scrivi sotto cosa stavi facendo, poi tocca **Riapri l'app**.",
			},
		],
	},
]

// I "?" sparsi per l'app: tre righe sul posto, e il rimando all'argomento che
// le spiega per intero. `topic` è l'id di un argomento qui sopra.
export const HELP_TIPS = {
	presenze: {
		title: "Presenze e recuperi",
		lines: [
			"Ogni abbonato nasce presente: si segna solo chi manca.",
			"Un'assenza è una lezione da recuperare, entro la stagione.",
			"Al posto di chi manca puoi mettere un sostituto: non cambia il prezzo della lezione.",
		],
		topic: "abbonamenti",
	},
	corsoSalta: {
		title: "Un corso che salta",
		lines: [
			"**Annulla** se doveva esserci: ogni abbonato ha una lezione da recuperare.",
			"**Annulla e riprogramma** se sai già quando recuperarla.",
			"**Elimina** solo se era stata inserita per sbaglio.",
		],
		topic: "abbonamenti",
	},
	compensi: {
		title: "Come si fanno i compensi",
		lines: [
			"Quello che fatturi al circolo e quello che incassi dagli allievi.",
			"Meno il costo dei campi, compresi quelli di quando giochi tu.",
			"Si calcolano dalle lezioni del periodo, e li vedi solo tu.",
		],
		topic: "conti",
	},
	chiPagaCampo: {
		title: "Chi paga il campo",
		lines: [
			"**Tu**: l'allievo paga il prezzo pieno, e il campo si toglie nei tuoi conti.",
			"**Gli allievi**: pagano il campo al circolo, e il prezzo che chiedi scende della loro quota.",
			"Vale da oggi, e ogni lezione può fare diversamente.",
		],
		topic: "conti",
	},
	ruoli: {
		title: "I ruoli",
		lines: [
			"**Collega** segna le sue lezioni; **osservatore** vede quelle di tutti.",
			"**Gestore** le modifica tutte, fa entrare colleghi e cambia il circolo.",
			"Con **Personalizza** scegli voce per voce. Chi non scende in campo lo spegni da **Insegna in questo circolo**, nella sua scheda.",
		],
		topic: "circolo",
	},
}

export const guideTopic = id => GUIDE_TOPICS.find(topic => topic.id === id) ?? null
